import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router";
import { enquiriesApi } from "@/lib/enquiries";
import { EnquiryStatusBadge } from "@/components/enquiries/enquiry-status-badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/app/layouts/PageContainer";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

function OverviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value ?? "—"}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkeletonDetail
// ---------------------------------------------------------------------------

function SkeletonDetail() {
  return (
    <PageContainer>
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 rounded bg-muted" />
        <div className="h-4 w-48 rounded bg-muted" />
        <div className="h-64 rounded bg-muted" />
      </div>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// EnquiryDetailPage
// ---------------------------------------------------------------------------

export function EnquiryDetailPage() {
  const { id = "0" } = useParams();
  const enqId = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["enquiry", enqId],
    queryFn: () => enquiriesApi.get(enqId),
    enabled: !!enqId,
  });

  const itemsQuery = useQuery({
    queryKey: ["enquiry-items", enqId],
    queryFn: () => enquiriesApi.items(enqId),
    enabled: !!enqId,
  });

  const convert = useMutation({
    mutationFn: () => enquiriesApi.convertToQuote(enqId),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["enquiry", enqId] });
      void qc.invalidateQueries({ queryKey: ["enquiries"] });
      toast.success(`Quote ${res.quoteRef} created.`);
      nav(`/quotes/${res.quoteId}`);
    },
    onError: (err: Error) => toast.error(`Convert failed: ${err.message}`),
  });

  if (isLoading) return <SkeletonDetail />;
  if (isError || !data) {
    return (
      <PageContainer>
        <p className="p-6 text-sm text-muted-foreground">Enquiry not found.</p>
      </PageContainer>
    );
  }

  const canConvert = data.status === "New" || data.status === "InQuote";

  return (
    <PageContainer>
      {/* Header */}
      <header className="mb-6 flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-2xl font-semibold">Enquiry #{data.enquiryId}</h1>
            {data.clientName && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-base text-muted-foreground">{data.clientName}</span>
              </>
            )}
            <span className="text-muted-foreground">·</span>
            <EnquiryStatusBadge status={data.status} />
          </div>
          {data.description && (
            <p className="text-sm text-muted-foreground truncate">{data.description}</p>
          )}
        </div>

        {/* Primary action */}
        <div className="flex gap-2 shrink-0">
          <Button
            onClick={() => convert.mutate()}
            disabled={!canConvert || convert.isPending}
            aria-label="Convert to Quote"
          >
            {convert.isPending ? "Converting…" : "Convert to Quote"}
          </Button>
        </div>
      </header>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="linked-quotes">Linked Quotes</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <OverviewRow label="Description" value={data.description} />
            <OverviewRow label="Source system" value={data.sourceSystem} />
            <OverviewRow label="External ID" value={data.externalId} />
            <OverviewRow label="Client" value={data.clientName} />
            <OverviewRow label="Owner" value={data.ownerEmployeeName} />
            <OverviewRow
              label="Branch"
              value={data.branchId != null ? `Branch #${data.branchId}` : null}
            />
            <OverviewRow
              label="Created"
              value={
                data.createdOn
                  ? new Date(data.createdOn).toLocaleDateString("en-AU")
                  : null
              }
            />
          </dl>
        </TabsContent>

        {/* Items */}
        <TabsContent value="items">
          <div className="mt-4">
            {itemsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading items…</p>
            ) : (itemsQuery.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No items on this enquiry.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Description</th>
                    <th className="text-right py-2 w-24">Qty</th>
                    <th className="text-left py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(itemsQuery.data ?? []).map((item) => (
                    <tr key={item.enquiryItemId} className="border-t">
                      <td className="py-1">{item.description}</td>
                      <td className="py-1 text-right">{item.quantity ?? "—"}</td>
                      <td className="py-1">{item.notes ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Linked Quotes */}
        <TabsContent value="linked-quotes">
          <div className="mt-4">
            <p className="text-sm text-muted-foreground mb-2">
              Quotes linked to this enquiry.
            </p>
            {/* Navigate to Quotes list filtered by enquiryId */}
            <Link
              to={`/quotes?enquiryId=${data.enquiryId}`}
              className="text-sm text-primary underline"
            >
              View quotes for enquiry #{data.enquiryId}
            </Link>
          </div>
        </TabsContent>

        {/* History */}
        <TabsContent value="history">
          <div className="mt-4">
            <p className="text-sm text-muted-foreground">
              Audit history is available in a future plan.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
